"use client";

import { upgradeUserToPro } from "@/actions/admin-actions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "@/components/ui/use-toast";
import { Crown } from "lucide-react";
import { useState } from "react";

interface UpgradeUserButtonProps {
  userId: string;
  variant?: "default" | "table";
}

export function UpgradeUserButton({
  userId,
  variant = "default",
}: UpgradeUserButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [duration, setDuration] = useState<"month" | "year">("month");
  const [isLoading, setIsLoading] = useState(false);

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const result = await upgradeUserToPro(userId, duration);
      if (result.success) {
        toast({
          title: "Success",
          description: `User upgraded to pro for 1 ${duration}`,
        });
        setIsOpen(false);
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to upgrade user",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upgrade user",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant={variant === "table" ? "ghost" : "default"}
          size={variant === "table" ? "icon" : "default"}
          className={variant === "table" ? "h-8 w-8" : ""}
        >
          {variant === "table" ? (
            <Crown className="h-4 w-4" />
          ) : (
            <>
              <Crown className="mr-2 h-4 w-4" />
              Upgrade to Pro
            </>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upgrade User to Pro</DialogTitle>
          <DialogDescription>
            Choose the duration for the free pro subscription
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <RadioGroup
            value={duration}
            onValueChange={(value) => setDuration(value as "month" | "year")}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="month" id="month" />
              <Label htmlFor="month">1 Month</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="year" id="year" />
              <Label htmlFor="year">1 Year</Label>
            </div>
          </RadioGroup>
          <Button onClick={handleUpgrade} disabled={isLoading}>
            {isLoading ? "Upgrading..." : "Confirm Upgrade"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
